<template>
  <div class="dashboard-activity-heatmap" :class="{'dashboard-activity-heatmap--compact': compact}">
    <div class="heatmap-header">
      <div class="heatmap-header-left">
        <div>
          <h3 class="heatmap-title">{{ title || $t('org-dashboard.activity-heatmap') }}</h3>
          <p v-if="!compact" class="heatmap-subtitle">{{ subtitle || $t('org-dashboard.heatmap-subtitle') }}</p>
        </div>
        <div class="heatmap-divider"></div>
        <div class="heatmap-usage">
          <span class="heatmap-usage-label">{{ $t('org-dashboard.usage-rate') }}</span>
          <span class="heatmap-usage-value">{{ computedUsageRate }}%</span>
        </div>
      </div>
      <div class="heatmap-header-right">
        <!-- Mode toggle (absolute / relative) -->
        <div v-if="hasOccupancyData" class="heatmap-control">
          <v-btn-toggle v-model="mode" mandatory dense class="heatmap-btn-toggle">
            <v-btn value="absolute" x-small>{{ $t('org-dashboard.heatmap-mode-absolute') }}</v-btn>
            <v-btn value="relative" x-small>{{ $t('org-dashboard.heatmap-mode-relative') }}</v-btn>
          </v-btn-toggle>
        </div>
        <!-- Time slot presets -->
        <div v-if="!compact" class="heatmap-control">
          <v-btn-toggle v-model="slotPreset" mandatory dense class="heatmap-btn-toggle">
            <v-btn value="24/7" x-small>24/7</v-btn>
            <v-btn value="business" x-small>{{ $t('org-dashboard.heatmap-slot-business') }}</v-btn>
            <v-btn value="daytime" x-small>{{ $t('org-dashboard.heatmap-slot-daytime') }}</v-btn>
            <v-btn value="custom" x-small><v-icon x-small>mdi-cog</v-icon></v-btn>
          </v-btn-toggle>
        </div>
        <!-- Legend -->
        <div class="heatmap-legend">
          <span class="heatmap-legend-label">{{ mode === 'relative' ? $t('org-dashboard.heatmap-occupancy') : 'Sessions' }}</span>
          <div class="heatmap-legend-scale">
            <div v-for="level in 5" :key="level" class="heatmap-legend-cell" :style="{backgroundColor: getLegendColor(level)}"></div>
          </div>
          <span v-if="mode === 'relative'" class="heatmap-legend-range">0% → {{ maxOccupancyLabel }}</span>
        </div>
      </div>
    </div>

    <!-- Custom time slot config -->
    <transition name="slot-expand">
      <div v-if="slotPreset === 'custom' && !compact" class="heatmap-custom-config">
        <div class="custom-days">
          <span class="custom-label">{{ $t('org-dashboard.heatmap-custom-days') }}</span>
          <label v-for="d in 7" :key="d" class="custom-day-check">
            <input type="checkbox" :value="d" v-model="customDays" />
            <span>{{ dayNameShort(d) }}</span>
          </label>
        </div>
        <div class="custom-hours">
          <span class="custom-label">{{ $t('org-dashboard.heatmap-custom-hours') }}</span>
          <select v-model.number="customStartHour" class="custom-select">
            <option v-for="h in 24" :key="h" :value="h - 1">{{ String(h - 1).padStart(2, '0') }}:00</option>
          </select>
          <button type="button" class="custom-swap" @click="swapHours" :title="$t('org-dashboard.heatmap-swap-hours')">⇄</button>
          <select v-model.number="customEndHour" class="custom-select">
            <option v-for="h in 24" :key="h" :value="h">{{ String(h).padStart(2, '0') }}:00</option>
          </select>
        </div>
      </div>
    </transition>

    <div class="heatmap-grid-wrap">
      <div class="heatmap-grid-container">
        <!-- Hour headers -->
        <div class="heatmap-row heatmap-row--header">
          <div class="heatmap-day-label">{{ $t('org-dashboard.hour') }}</div>
          <div class="heatmap-hours">
            <div
              v-for="h in 24"
              :key="h"
              class="heatmap-hour-label"
              :class="{
                'heatmap-hour-label--highlight': [6, 12, 18].includes(h - 1),
                'heatmap-hour-label--dimmed': !isHourActive(h - 1),
              }"
            >
              {{ String(h - 1).padStart(2, '0') }}
            </div>
          </div>
        </div>

        <!-- Day rows -->
        <div v-for="(row, di) in processedRows" :key="di" class="heatmap-row">
          <div class="heatmap-day-label" :class="{'heatmap-day-label--dimmed': !isDayActive(row.dayIndex)}">{{ row.day }}</div>
          <div class="heatmap-hours">
            <div
              v-for="(cell, hi) in row.cells"
              :key="hi"
              class="heatmap-cell"
              :class="{'heatmap-cell--inactive': !cell.active}"
              :style="{backgroundColor: cell.color}"
              @mouseenter="hoveredCell = {day: row.day, hour: hi, cell, rowIndex: di}"
              @mouseleave="hoveredCell = null"
            >
              <div
                v-if="hoveredCell && hoveredCell.day === row.day && hoveredCell.hour === hi"
                class="heatmap-tooltip"
                :class="{'heatmap-tooltip--below': di === 0}"
              >
                {{ formatTooltip(row.day, hi, cell) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="heatmap-footer">
      <span class="heatmap-timezone">{{ $t('org-dashboard.heatmap-timezone') }} : {{ timezoneName }}</span>
    </div>
  </div>
</template>

<script>
const COLORS_ABSOLUTE = ['#FFF7ED', '#FDBA74', '#FB923C', '#EA580C', '#B91C1C']
const COLORS_RELATIVE = ['#f3f4f6', '#86efac', '#facc15', '#fb923c', '#ef4444']
const COLOR_INACTIVE = '#f3f4f6'

export default {
  name: 'DashboardActivityHeatmap',

  props: {
    // Raw cell data from API: [{day: 1-7, hour: 0-23, count/sessions, occupancyRate?}]
    rawData: {
      type: Array,
      default: () => [],
    },
    // Legacy pre-normalized format: [{day: "Lun", hours: [0..1, ...]}]
    data: {
      type: Array,
      default: () => [],
    },
    usageRate: {
      type: [Number, String],
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    subtitle: {
      type: String,
      default: null,
    },
    // Compact mode for drawers (no time slot filter, smaller)
    compact: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      hoveredCell: null,
      mode: localStorage.getItem('heatmap_mode') || 'absolute',
      slotPreset: localStorage.getItem('heatmap_slotPreset') || '24/7',
      customDays: JSON.parse(localStorage.getItem('heatmap_customDays') || '[1,2,3,4,5]'),
      customStartHour: parseInt(localStorage.getItem('heatmap_customStartHour') || '8'),
      customEndHour: parseInt(localStorage.getItem('heatmap_customEndHour') || '18'),
    }
  },

  computed: {
    // Detect if we're using rawData or legacy format
    useRawData() {
      return this.rawData && this.rawData.length > 0
    },

    // Timezone offset in whole hours (e.g. UTC+1 = 1, UTC-5 = -5)
    timezoneOffsetHours() {
      return Math.round(-new Date().getTimezoneOffset() / 60)
    },

    // User's timezone name (e.g. "Europe/Paris")
    timezoneName() {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
      } catch {
        const h = this.timezoneOffsetHours
        return `UTC${h >= 0 ? '+' : ''}${h}`
      }
    },

    // Raw data shifted from GMT to user's local timezone
    shiftedRawData() {
      const offset = this.timezoneOffsetHours
      if (offset === 0 || !this.rawData || !this.rawData.length) return this.rawData
      return this.rawData.map(entry => {
        let hour = entry.hour + offset
        let day = entry.day
        if (hour >= 24) { hour -= 24; day = day < 7 ? day + 1 : 1 }
        else if (hour < 0) { hour += 24; day = day > 1 ? day - 1 : 7 }
        return { ...entry, day, hour }
      })
    },

    // Check if occupancyRate is available in the data
    hasOccupancyData() {
      if (!this.useRawData) return false
      return this.rawData.some(entry => entry.occupancyRate != null)
    },

    // Build normalized cell data (from timezone-shifted data)
    cellMap() {
      const map = {}
      if (!this.shiftedRawData || !this.shiftedRawData.length) return map

      this.shiftedRawData.forEach(entry => {
        const key = `${entry.day}_${entry.hour}`
        map[key] = {
          count: entry.count || entry.sessions || 0,
          occupancyRate: entry.occupancyRate ?? null,
        }
      })
      return map
    },

    maxCount() {
      if (!this.useRawData) return 1
      return Math.max(...this.shiftedRawData.map(r => r.count || r.sessions || 0), 1)
    },

    maxOccupancy() {
      if (!this.useRawData) return 1
      const max = Math.max(...this.shiftedRawData.map(r => r.occupancyRate || 0), 0)
      return max > 0 ? max : 1
    },

    maxOccupancyLabel() {
      return Math.round(this.maxOccupancy * 100) + '%'
    },

    // Active days based on preset
    activeDays() {
      switch (this.slotPreset) {
        case 'business': return [1, 2, 3, 4, 5]
        case 'daytime': return [1, 2, 3, 4, 5, 6, 7]
        case 'custom': return this.customDays
        default: return [1, 2, 3, 4, 5, 6, 7]
      }
    },

    // Active hour range based on preset
    activeHourRange() {
      switch (this.slotPreset) {
        case 'business': return [8, 18]
        case 'daytime': return [8, 18]
        case 'custom': return [this.customStartHour, this.customEndHour]
        default: return [0, 24]
      }
    },

    activeHoursCount() {
      const [start, end] = this.activeHourRange
      if (start <= end) return end - start
      // Nocturne wrap: e.g. 18→8 = 6 hours (18-23) + 8 hours (0-7) = 14
      return (24 - start) + end
    },

    colorScale() {
      return this.mode === 'relative' ? COLORS_RELATIVE : COLORS_ABSOLUTE
    },

    processedRows() {
      // Legacy format takes priority when it has data (drawers)
      if (!this.useRawData && this.data && this.data.length > 0) {
        return this.buildRowsFromLegacy()
      }
      // Raw data mode — always builds 7 rows even when empty
      return this.buildRowsFromRaw()
    },

    computedUsageRate() {
      if (this.useRawData) {
        return this.calculateFilteredUsageRate()
      }
      // Legacy: use prop or recalculate for active cells
      if (this.usageRate != null && this.slotPreset === '24/7') {
        return this.usageRate
      }
      // Recalculate from legacy data for filtered view
      const rows = this.processedRows
      let sum = 0
      let count = 0
      rows.forEach(row => {
        row.cells.forEach(cell => {
          if (cell.active) {
            sum += cell.normalizedValue
            count++
          }
        })
      })
      if (count === 0) return 0
      return Math.round((sum / count) * 100)
    },
  },

  watch: {
    mode(val) {
      localStorage.setItem('heatmap_mode', val)
    },
    slotPreset(val) {
      localStorage.setItem('heatmap_slotPreset', val)
    },
    customDays(val) {
      localStorage.setItem('heatmap_customDays', JSON.stringify(val))
    },
    customStartHour(val) {
      localStorage.setItem('heatmap_customStartHour', String(val))
    },
    customEndHour(val) {
      localStorage.setItem('heatmap_customEndHour', String(val))
    },
  },

  methods: {
    swapHours() {
      const tmp = this.customStartHour
      this.customStartHour = this.customEndHour
      this.customEndHour = tmp
    },

    dayNameShort(d) {
      const keys = ['day-mon', 'day-tue', 'day-wed', 'day-thu', 'day-fri', 'day-sat', 'day-sun']
      return this.$t('org-dashboard.' + keys[d - 1])
    },

    isDayActive(dayIndex) {
      if (this.compact) return true
      return this.activeDays.includes(dayIndex)
    },

    isHourActive(hour) {
      if (this.compact) return true
      const [start, end] = this.activeHourRange
      if (start <= end) {
        return hour >= start && hour < end
      }
      // Nocturne wrap: e.g. 18h→8h = 18-23 + 0-7
      return hour >= start || hour < end
    },

    isCellActive(dayIndex, hour) {
      return this.isDayActive(dayIndex) && this.isHourActive(hour)
    },

    getColor(value, active) {
      if (!active) return COLOR_INACTIVE
      if (!value || value <= 0) return this.colorScale[0]
      const idx = Math.min(Math.floor(value * (this.colorScale.length - 1)), this.colorScale.length - 1)
      return this.colorScale[idx]
    },

    getLegendColor(level) {
      return this.colorScale[Math.min(level, this.colorScale.length - 1)]
    },

    buildRowsFromRaw() {
      return [1, 2, 3, 4, 5, 6, 7].map(d => {
        const cells = []
        for (let h = 0; h < 24; h++) {
          const key = `${d}_${h}`
          const entry = this.cellMap[key] || { count: 0, occupancyRate: null }
          const active = this.isCellActive(d, h)
          let normalizedValue

          if (this.mode === 'relative' && entry.occupancyRate != null) {
            // Occupation: normalize by max observed for color contrast, tooltip shows real %
            normalizedValue = this.maxOccupancy > 0 ? entry.occupancyRate / this.maxOccupancy : 0
          } else {
            // Absolute: normalize count by max
            normalizedValue = entry.count / this.maxCount
          }

          cells.push({
            count: entry.count,
            occupancyRate: entry.occupancyRate,
            normalizedValue,
            active,
            color: this.getColor(normalizedValue, active),
          })
        }
        return {
          day: this.dayNameShort(d),
          dayIndex: d,
          cells,
        }
      })
    },

    buildRowsFromLegacy() {
      return this.data.map((row, di) => {
        const dayIndex = di + 1
        const cells = (row.hours || []).map((val, hi) => {
          const active = this.isCellActive(dayIndex, hi)
          return {
            count: 0,
            occupancyRate: null,
            normalizedValue: val || 0,
            active,
            color: this.getColor(val || 0, active),
          }
        })
        return {
          day: row.day,
          dayIndex,
          cells,
        }
      })
    },

    calculateFilteredUsageRate() {
      // Always use occupancyRate when available (mode-independent)
      const data = this.shiftedRawData
      const hasOccupancy = data.some(e => e.occupancyRate != null)
      const totalActiveCells = this.activeDays.length * this.activeHoursCount
      if (totalActiveCells === 0) return 0

      if (hasOccupancy) {
        let sum = 0
        data.forEach(entry => {
          if (entry.occupancyRate != null && this.isCellActive(entry.day, entry.hour)) {
            sum += entry.occupancyRate
          }
        })
        return Math.round((sum / totalActiveCells) * 100)
      }

      // Fallback: average intensity relative to max
      let sum = 0
      data.forEach(entry => {
        if (this.isCellActive(entry.day, entry.hour)) {
          sum += (entry.count || entry.sessions || 0)
        }
      })
      return Math.round((sum / (totalActiveCells * this.maxCount)) * 100)
    },

    formatTooltip(dayName, hour, cell) {
      const hourStr = String(hour).padStart(2, '0') + ':00'
      if (this.mode === 'relative' && cell.occupancyRate != null) {
        return `${dayName} ${hourStr} — ${Math.round(cell.occupancyRate * 100)}%`
      }
      const s = cell.count === 1 ? 'session' : 'sessions'
      return `${dayName} ${hourStr} — ${cell.count} ${s}`
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-activity-heatmap {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  overflow: visible;

  &--compact {
    padding: 12px;
    border: none;
    box-shadow: none;
    border-radius: 8px;
    background: #fafafa;
  }
}

.heatmap-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

.heatmap-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.heatmap-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.heatmap-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;

  .dashboard-activity-heatmap--compact & {
    font-size: 12px;
  }
}

.heatmap-subtitle {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}

.heatmap-footer {
  margin-top: 8px;
  text-align: right;
}

.heatmap-timezone {
  font-size: 10px;
  color: #9ca3af;
  font-style: italic;
}

.heatmap-divider {
  width: 1px;
  height: 40px;
  background: #e5e7eb;
}

.heatmap-usage-label {
  display: block;
  font-size: 10px;
  color: #9ca3af;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.heatmap-usage-value {
  font-size: 18px;
  font-weight: 700;
  color: #FF5722;

  .dashboard-activity-heatmap--compact & {
    font-size: 14px;
  }
}

.heatmap-control {
  display: flex;
  align-items: center;
}

.heatmap-btn-toggle {
  &.v-btn-toggle {
    border-radius: 6px;
    .v-btn {
      height: 28px !important;
      font-size: 11px !important;
      font-weight: 600;
      text-transform: none;
      letter-spacing: 0;
      padding: 0 8px !important;
      min-width: auto !important;
    }
    .v-btn--active {
      background-color: #FF5722 !important;
      color: white !important;

      .v-icon {
        color: white !important;
      }
    }
  }
}

.heatmap-legend {
  display: flex;
  align-items: center;
  gap: 6px;
}

.heatmap-legend-label {
  font-size: 10px;
  color: #9ca3af;
  text-transform: uppercase;
  font-weight: 700;
}

.heatmap-legend-scale {
  display: flex;
  gap: 3px;
}

.heatmap-legend-cell {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.heatmap-legend-range {
  font-size: 9px;
  color: #9ca3af;
  white-space: nowrap;
}

/* Custom time slot config */
.heatmap-custom-config {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.custom-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  margin-right: 6px;
}

.custom-days {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.custom-day-check {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: #374151;
  cursor: pointer;

  input[type="checkbox"] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    border: 1.5px solid #d1d5db;
    border-radius: 3px;
    background: white;
    margin: 0;
    cursor: pointer;
    position: relative;
    transition: all 0.15s ease;

    &:checked {
      background-color: #FF5722;
      border-color: #FF5722;
    }

    &:checked::after {
      content: '';
      position: absolute;
      left: 3.5px;
      top: 1px;
      width: 4px;
      height: 7px;
      border: solid white;
      border-width: 0 1.5px 1.5px 0;
      transform: rotate(45deg);
    }

    &:hover {
      border-color: #FF5722;
    }
  }
}

.custom-hours {
  display: flex;
  align-items: center;
  gap: 6px;
}

.custom-select {
  font-size: 11px;
  padding: 2px 4px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  color: #374151;
  background: white;
  cursor: pointer;
}

.custom-swap {
  font-size: 14px;
  color: #6b7280;
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: #FF5722;
    border-color: #FF5722;
    background: #FFF7ED;
  }
}

.slot-expand-enter-active,
.slot-expand-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}

.slot-expand-enter,
.slot-expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.slot-expand-enter-to,
.slot-expand-leave {
  opacity: 1;
  max-height: 80px;
}

/* Grid */
.heatmap-grid-wrap {
  overflow-x: auto;
  padding-bottom: 4px;
}

.heatmap-grid-container {
  min-width: 700px;

  .dashboard-activity-heatmap--compact & {
    min-width: 400px;
  }
}

.heatmap-row {
  display: grid;
  grid-template-columns: 60px 1fr;
  align-items: center;
  margin-bottom: 3px;

  &--header {
    margin-bottom: 6px;
  }

  .dashboard-activity-heatmap--compact & {
    grid-template-columns: 40px 1fr;
    margin-bottom: 2px;
  }
}

.heatmap-day-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-align: right;
  padding-right: 12px;

  &--dimmed {
    color: #d1d5db;
  }

  .dashboard-activity-heatmap--compact & {
    font-size: 10px;
    padding-right: 6px;
  }
}

.heatmap-hours {
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  gap: 3px;

  .dashboard-activity-heatmap--compact & {
    gap: 1px;
  }
}

.heatmap-hour-label {
  font-size: 10px;
  color: #9ca3af;
  text-align: center;

  &--highlight {
    color: #4b5563;
    font-weight: 700;
  }

  &--dimmed {
    color: #d1d5db;
  }

  .dashboard-activity-heatmap--compact & {
    font-size: 8px;
  }
}

.heatmap-cell {
  aspect-ratio: 1;
  border-radius: 2px;
  cursor: pointer;
  transition: all 0.15s ease-out;
  position: relative;

  &:hover {
    transform: scale(1.4);
    z-index: 10;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }

  &--inactive {
    opacity: 0.35;

    &:hover {
      transform: scale(1.2);
    }
  }
}

.heatmap-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #1f2937;
  color: white;
  font-size: 10px;
  font-weight: 500;
  padding: 3px 6px;
  border-radius: 4px;
  white-space: nowrap;
  z-index: 20;
  pointer-events: none;
  margin-bottom: 4px;

  &--below {
    bottom: auto;
    top: 100%;
    margin-bottom: 0;
    margin-top: 4px;
  }
}
</style>
